import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CloudUploadOutlinedIcon from "@mui/icons-material/CloudUploadOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import DownloadOutlinedIcon from "@mui/icons-material/DownloadOutlined";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import Box from "@mui/material/Box";
import Chip from "@mui/material/Chip";
import CircularProgress from "@mui/material/CircularProgress";
import FormHelperText from "@mui/material/FormHelperText";
import IconButton from "@mui/material/IconButton";
import Typography from "@mui/material/Typography";
import { styled } from "@mui/material/styles";
import { useId } from "react";

const VisuallyHiddenInput = styled("input")({
  clip: "rect(0 0 0 0)",
  clipPath: "inset(50%)",
  height: 1,
  overflow: "hidden",
  position: "absolute",
  left: 0,
  whiteSpace: "nowrap",
  width: 1,
});

const emptyCardSx = {
  display: "flex",
  alignItems: "center",
  gap: 1.25,
  py: 1,
  px: 1.5,
  borderRadius: 2,
  border: "1px solid",
  borderColor: "#b8d4ea",
  bgcolor: "#eef4fa",
  minHeight: 0,
  width: "100%",
  maxWidth: "100%",
  boxSizing: "border-box",
  transition: "box-shadow 0.2s, border-color 0.2s",
};

const loadedCardSx = {
  display: "flex",
  alignItems: "flex-start",
  gap: 1.25,
  py: 1,
  px: 1.5,
  borderRadius: 2,
  border: "1px solid",
  borderColor: "#a5d6a7",
  bgcolor: "#f1f8f2",
  minHeight: 0,
  width: "100%",
  maxWidth: "100%",
  boxSizing: "border-box",
};

function formatFileSize(bytes) {
  if (!Number.isFinite(bytes) || bytes < 0) return "";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function formatDisplayedFileName(fileName, maxBaseLength) {
  if (!fileName) return "archivo";

  const trimmedFileName = fileName.trim();
  if (!trimmedFileName) return "archivo";
  if (
    maxBaseLength === undefined ||
    !Number.isFinite(maxBaseLength) ||
    maxBaseLength < 1
  ) {
    return trimmedFileName;
  }

  const lastDotIndex = trimmedFileName.lastIndexOf(".");
  const hasValidExtension =
    lastDotIndex > 0 && lastDotIndex < trimmedFileName.length - 1;
  const baseName = hasValidExtension
    ? trimmedFileName.slice(0, lastDotIndex)
    : trimmedFileName;
  const extension = hasValidExtension
    ? trimmedFileName.slice(lastDotIndex)
    : "";

  if (baseName.length <= maxBaseLength) return trimmedFileName;
  return `${baseName.slice(0, maxBaseLength)}...${extension}`;
}

export function AdjuntoFileCard({
  title,
  idTipoAdjunto,
  hasFile,
  loading = false,
  fileName,
  fileSizeBytes,
  accept = ".pdf,.jpg,.jpeg,.png",
  formatHint,
  disabled = false,
  variant = "default",
  showStatusChip = true,
  statusChipLabel = "CARGADO",
  maxTitleLines = 1,
  maxFilenameLines = 1,
  maxFileNameChars,
  showReplaceAction: _showReplaceAction = true,
  showRemoveAction = true,
  showDownloadAction = false,
  showViewAction = false,
  error = false,
  sx,
  onFileChange,
  onRemove: onRemoveFile,
  onDownload,
  onView,
}) {
  const inputId = useId();
  const editBlocked = disabled && !loading;
  const busy = loading;
  const sizeLabel =
    fileSizeBytes != null && Number.isFinite(fileSizeBytes)
      ? formatFileSize(fileSizeBytes)
      : "";
  const densitySx =
    variant === "compact"
      ? {
          py: 0.75,
          px: 1,
          gap: 0.75,
        }
      : variant === "comfortable"
        ? {
            py: 1.25,
            px: 1.75,
            gap: 1.5,
          }
        : {};

  /** Toda la tarjeta vacía actúa como selector de archivo (sin texto «Cargar archivo»). */
  const emptyWholeCardIsLabel = !hasFile && !loading && !editBlocked;
  const allowRemove = showRemoveAction && !editBlocked;
  const allowDownload =
    showDownloadAction && Boolean(onDownload) && hasFile && !loading;
  const allowView = showViewAction && Boolean(onView) && hasFile && !loading;

  return (
    <Box
      component={emptyWholeCardIsLabel ? "label" : "div"}
      className={error ? "Mui-error" : undefined}
      aria-invalid={error || undefined}
      {...(emptyWholeCardIsLabel ? { htmlFor: inputId } : {})}
      sx={[
        hasFile ? loadedCardSx : emptyCardSx,
        densitySx,
        {
          opacity: editBlocked && !hasFile ? 0.65 : 1,
          overflow: "hidden",
          pointerEvents: busy ? "none" : "auto",
          ...(emptyWholeCardIsLabel ? { cursor: "pointer" } : {}),
          ...(error
            ? {
                borderColor: "error.main",
                bgcolor: hasFile ? loadedCardSx.bgcolor : "#fdecea",
                boxShadow: "0 0 0 1px #d32f2f",
              }
            : {}),
        },
        ...(sx ? (Array.isArray(sx) ? sx : [sx]) : []),
      ]}
    >
      {emptyWholeCardIsLabel ? (
        <VisuallyHiddenInput
          id={inputId}
          type="file"
          accept={accept}
          disabled={busy || editBlocked}
          onChange={(e) => onFileChange(e, idTipoAdjunto)}
        />
      ) : null}
      <Box
        sx={{
          flexShrink: 0,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          lineHeight: 0,
        }}
      >
        {loading ? (
          <CircularProgress size={22} thickness={4} sx={{ color: "#1976D2" }} />
        ) : hasFile ? (
          <CheckCircleIcon sx={{ fontSize: 26, color: "#2e7d32" }} />
        ) : (
          <CloudUploadOutlinedIcon sx={{ fontSize: 26, color: "#1976D2" }} />
        )}
      </Box>

      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            flexWrap: "wrap",
            mb: hasFile && !loading ? 0.5 : 0,
          }}
        >
          <Typography
            component="span"
            sx={{
              fontWeight: 700,
              color: "#1a2b3c",
              fontSize: "0.9rem",
              lineHeight: 1.25,
              display: "-webkit-box",
              WebkitLineClamp: maxTitleLines,
              WebkitBoxOrient: "vertical",
              overflow: "hidden",
              wordBreak: "break-word",
            }}
          >
            {title}
          </Typography>
          {showStatusChip && hasFile && !loading ? (
            <Chip
              label={statusChipLabel}
              size="small"
              sx={{
                height: 22,
                fontSize: "0.65rem",
                fontWeight: 700,
                letterSpacing: 0.5,
                bgcolor: "#c8e6c9",
                color: "#1b5e20",
                "& .MuiChip-label": { px: 1 },
              }}
            />
          ) : null}
        </Box>

        {!hasFile && !loading ? (
          <>
            {formatHint ? (
              <Typography
                variant="caption"
                display="block"
                sx={{ color: "text.secondary", mt: 0.5 }}
              >
                {formatHint}
              </Typography>
            ) : null}
          </>
        ) : null}

        {hasFile && !loading ? (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              flexWrap: "wrap",
              mt: 0.25,
            }}
          >
            <Typography
              variant="body2"
              sx={{
                color: "text.secondary",
                minWidth: 0,
                display: "-webkit-box",
                WebkitLineClamp: maxFilenameLines,
                WebkitBoxOrient: "vertical",
                overflow: "hidden",
                wordBreak: "break-word",
              }}
            >
              {formatDisplayedFileName(fileName, maxFileNameChars)}
              {sizeLabel ? ` · ${sizeLabel}` : ""}
            </Typography>
          </Box>
        ) : null}
      </Box>

      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          flexShrink: 0,
          alignSelf: "center",
          gap: 0.25,
        }}
      >
        {allowView ? (
          <IconButton
            size="small"
            aria-label="Ver archivo"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              onView?.();
            }}
            sx={{ color: "#1976D2" }}
          >
            <VisibilityOutlinedIcon fontSize="small" />
          </IconButton>
        ) : null}
        {allowDownload ? (
          <IconButton
            size="small"
            aria-label="Descargar archivo"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              onDownload?.();
            }}
            sx={{ color: "#1976D2" }}
          >
            <DownloadOutlinedIcon fontSize="small" />
          </IconButton>
        ) : null}
        {allowRemove && onRemoveFile ? (
          <IconButton
            size="small"
            aria-label="Quitar archivo"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              onRemoveFile();
            }}
            sx={{ color: "text.secondary" }}
          >
            <DeleteOutlineIcon fontSize="small" />
          </IconButton>
        ) : null}
      </Box>
    </Box>
  );
}

/**
 * Tarjeta de adjunto. En murcielago2 el formulario no usa react-hook-form:
 * el padre informa `error` (boolean o mensaje) y `required`.
 */
export function AdjuntoField({
  idTipoAdjunto,
  accept = ".pdf,.jpg,.jpeg,.png",
  loadingId,
  loadingKey,
  hasFile,
  title,
  fileName,
  fileSizeBytes,
  formatHint,
  disabled = false,
  required = false,
  error = false,
  cardProps,
  sx,
  onFileChange,
  onRemove: onRemoveFromList,
  onDownload,
  onView,
}) {
  const loading =
    loadingKey !== undefined
      ? loadingId === loadingKey
      : loadingId === idTipoAdjunto;

  const canDownload = Boolean(onDownload) && hasFile;
  const canView = Boolean(onView) && hasFile;
  const errorMessage = typeof error === "string" ? error : "";
  const hasError = Boolean(error);

  return (
    <>
      <AdjuntoFileCard
        title={`${title}${required ? " *" : ""}`}
        idTipoAdjunto={idTipoAdjunto}
        hasFile={hasFile}
        loading={loading}
        fileName={fileName}
        fileSizeBytes={fileSizeBytes}
        accept={accept}
        formatHint={formatHint}
        disabled={disabled || loading}
        variant={cardProps?.variant}
        showStatusChip={cardProps?.showStatusChip}
        statusChipLabel={cardProps?.statusChipLabel}
        maxTitleLines={cardProps?.maxTitleLines}
        maxFilenameLines={cardProps?.maxFilenameLines}
        maxFileNameChars={cardProps?.maxFileNameChars}
        showReplaceAction={cardProps?.showReplaceAction}
        showRemoveAction={cardProps?.showRemoveAction}
        showDownloadAction={
          cardProps?.showDownloadAction !== undefined
            ? cardProps.showDownloadAction && canDownload
            : canDownload
        }
        showViewAction={
          cardProps?.showViewAction !== undefined
            ? cardProps.showViewAction && canView
            : canView
        }
        error={hasError}
        sx={sx}
        onFileChange={onFileChange}
        onRemove={onRemoveFromList}
        onDownload={onDownload}
        onView={onView}
      />
      <FormHelperText
        error={hasError}
        sx={{ ml: 0.5, mt: 0.5, minHeight: 20, fontWeight: 500 }}
      >
        {errorMessage || " "}
      </FormHelperText>
    </>
  );
}
